import { useState } from "react";
import { motion } from "framer-motion";
import {
  Building2,
  Cog,
  Package,
  Wallet,
  TrendingUp,
  FileText,
  Clock,
  Truck,
  ChevronDown,
  ChevronUp,
  IndianRupee,
  MessageCircle,
  Share2
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const getSectionIcon = (id) => {
  const icons = {
    intro: FileText,
    process: Cog,
    land_building: Building2,
    machinery: Cog,
    raw_materials: Package,
    working_capital: Wallet,
    profitability: TrendingUp,
    registration: FileText,
  };
  return icons[id] || FileText;
};

const formatCurrency = (value) => {
  if (!value) return '₹0';
  return new Intl.NumberFormat('hi-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value);
};

const DPRSection = ({ section, index }) => {
  const [expanded, setExpanded] = useState(index < 3);
  const Icon = getSectionIcon(section.id);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="dpr-section mb-4"
      data-testid={`dpr-section-${section.id}`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
            <Icon className="w-5 h-5 text-orange-500" />
          </div>
          <div>
            <h3 className="font-rajdhani font-bold text-lg text-gray-800">
              {section.title_hindi}
            </h3>
            <p className="text-xs text-gray-8000">{section.title}</p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-gray-8000" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-8000" />
        )}
      </button>

      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          className="mt-4 pl-13"
        >
          <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
            {section.content_hindi || section.content}
          </p>

          {/* Cost Display */}
          {section.cost && (
            <div className="mt-4 p-3 bg-white rounded-lg">
              <span className="text-gray-8000 text-sm">अनुमानित लागत: </span>
              <span className="text-orange-500 font-bold font-tabular-nums">
                {formatCurrency(section.cost)}
              </span>
            </div>
          )}

          {/* Items List (Machinery, Raw Materials) */}
          {section.items && section.items.length > 0 && (
            <div className="mt-4 space-y-2">
              {section.items.map((item, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 bg-white rounded-lg"
                >
                  <span className="text-gray-600">{item.name}</span>
                  <span className="text-orange-500 font-tabular-nums">
                    {formatCurrency(item.cost || item.monthly_cost)}
                  </span>
                </div>
              ))}
              {section.total_cost && (
                <div className="flex items-center justify-between p-3 bg-orange-500/10 rounded-lg border border-orange-500/30">
                  <span className="text-gray-700 font-semibold">कुल</span>
                  <span className="text-orange-500 font-bold font-tabular-nums">
                    {formatCurrency(section.total_cost)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Profitability Stats */}
          {section.id === 'profitability' && section.monthly_profit && (
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="p-4 bg-white rounded-lg">
                <p className="text-xs text-gray-8000 mb-1">मासिक बिक्री</p>
                <p className="text-lg font-bold text-gray-700 font-tabular-nums">
                  {formatCurrency(section.monthly_sales)}
                </p>
              </div>
              <div className="p-4 bg-white rounded-lg">
                <p className="text-xs text-gray-8000 mb-1">मासिक खर्च</p>
                <p className="text-lg font-bold text-gray-700 font-tabular-nums">
                  {formatCurrency(section.monthly_expenses)}
                </p>
              </div>
              <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                <p className="text-xs text-emerald-400 mb-1">मासिक लाभ</p>
                <p className="text-lg font-bold text-emerald-500 font-tabular-nums">
                  {formatCurrency(section.monthly_profit)}
                </p>
              </div>
              <div className="p-4 bg-orange-500/10 rounded-lg border border-orange-500/30">
                <p className="text-xs text-orange-400 mb-1">ROI</p>
                <p className="text-lg font-bold text-orange-500">
                  {section.roi_percentage}%
                </p>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

const Timeline = ({ steps }) => {
  return (
    <div className="bg-white rounded-xl p-6 border border-gray-200">
      <h3 className="font-rajdhani font-bold text-xl text-gray-800 mb-6 flex items-center gap-2">
        <Clock className="w-5 h-5 text-orange-500" />
        उद्योग स्थापना टाइमलाइन
      </h3>
      <div className="space-y-0">
        {steps?.map((step, index) => (
          <div key={index} className="timeline-step" data-testid={`timeline-step-${index}`}>
            <div className="flex items-start justify-between">
              <div>
                <Badge className="bg-orange-500/20 text-orange-500 border-orange-500/30 mb-2">
                  चरण {step.step}
                </Badge>
                <h4 className="font-semibold text-gray-700">{step.title_hindi}</h4>
                <p className="text-sm text-gray-8000">{step.title}</p>
                <p className="text-sm text-gray-500 mt-2">{step.description_hindi}</p>
              </div>
              <span className="text-xs text-orange-500 bg-orange-500/10 px-2 py-1 rounded">
                {step.duration}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Suppliers = ({ suppliers, onShareSuppliers }) => {
  if (!suppliers || suppliers.length === 0) return null;

  return (
    <div className="bg-white rounded-xl p-6 border border-gray-200 mt-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-rajdhani font-bold text-xl text-gray-800 flex items-center gap-2">
          <Truck className="w-5 h-5 text-orange-500" />
          सप्लायर सुझाव
        </h3>
        <Button
          onClick={onShareSuppliers}
          size="sm"
          className="bg-green-600 hover:bg-green-700 rounded-sm text-sm"
          data-testid="share-suppliers-btn"
        >
          <MessageCircle className="w-4 h-4 mr-1" />
          WhatsApp पर भेजें
        </Button>
      </div>
      <div className="grid gap-4">
        {suppliers.map((supplier, index) => (
          <div
            key={index}
            className="p-4 bg-gray-50 rounded-lg border border-gray-200"
            data-testid={`supplier-${index}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-semibold text-gray-700">{supplier.name}</h4>
                <p className="text-sm text-gray-8000">{supplier.location}</p>
              </div>
              <Badge className="bg-gray-100 text-gray-500">{supplier.type}</Badge>
            </div>
            <p className="text-xs text-orange-500 mt-2">
              खोजें: {supplier.contact_hint}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export const DPRView = ({ dpr }) => {
  if (!dpr) return null;

  const formatCurrency = (value) => {
    if (!value) return '₹0';
    return new Intl.NumberFormat('hi-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const shareOnWhatsApp = (message) => {
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
    toast.success("WhatsApp पर शेयर करें");
  };

  const shareDPRSummary = () => {
    let message = `📋 *${dpr.project_name_hindi} - प्रोजेक्ट रिपोर्ट*\n\n`;
    message += `💰 *कुल लागत:* ${formatCurrency(dpr.total_cost)}\n`;
    message += `📊 *श्रेणी:* ${dpr.category}\n\n`;
    
    const profitSection = dpr.sections?.find(s => s.id === 'profitability');
    if (profitSection) {
      message += `💹 *मुनाफा विश्लेषण:*\n`;
      if (profitSection.monthly_sales) message += `• मासिक बिक्री: ${formatCurrency(profitSection.monthly_sales)}\n`;
      if (profitSection.monthly_profit) message += `• मासिक लाभ: ${formatCurrency(profitSection.monthly_profit)}\n`;
      if (profitSection.roi_percentage) message += `• ROI: ${profitSection.roi_percentage}%\n\n`;
    }
    
    message += `🔗 पूरी रिपोर्ट के लिए: ${window.location.href}\n\n`;
    message += `---\n*उद्यमी मित्र* - अपना उद्योग शुरू करें! 🚀`;
    
    shareOnWhatsApp(message);
  };

  const shareSuppliers = () => {
    if (!dpr.suppliers || dpr.suppliers.length === 0) {
      toast.error("कोई सप्लायर जानकारी उपलब्ध नहीं");
      return;
    }
    
    let message = `📦 *${dpr.project_name_hindi} - सप्लायर लिस्ट*\n\n`;
    
    dpr.suppliers.forEach((supplier, index) => {
      message += `${index + 1}. *${supplier.name}*\n`;
      message += `   📍 ${supplier.location}\n`;
      message += `   🏷️ ${supplier.type}\n`;
      message += `   🔍 ${supplier.contact_hint}\n\n`;
    });
    
    message += `---\n*उद्यमी मित्र* - सप्लायर खोजने में मदद 🤝`;
    
    shareOnWhatsApp(message);
  };

  return (
    <div className="space-y-8">
      {/* Sections */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-rajdhani font-bold text-2xl text-gray-800">
            विस्तृत प्रोजेक्ट रिपोर्ट (DPR)
          </h2>
          <Button
            onClick={shareDPRSummary}
            className="bg-green-600 hover:bg-green-700 rounded-sm"
            data-testid="share-dpr-btn"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            DPR शेयर करें
          </Button>
        </div>
        {dpr.sections?.map((section, index) => (
          <DPRSection key={section.id || index} section={section} index={index} />
        ))}
      </div>

      {/* Timeline */}
      {dpr.timeline_steps && dpr.timeline_steps.length > 0 && (
        <Timeline steps={dpr.timeline_steps} />
      )}

      {/* Suppliers */}
      <Suppliers suppliers={dpr.suppliers} onShareSuppliers={shareSuppliers} />
    </div>
  );
};
